import { Edit2, Upload } from 'lucide-react';
import { useState } from 'react';
import { QuillEditor } from '../quill_editor/QuillEditor';

export interface FormData {
  id?: string;
  title?: string;
  image?: string | File | null;
  status?: boolean;
  url?: string;
  description?: string;
};

interface FormErrors {
  title?: string;
  image?: string;
  status?: string;
  url?: string;
  description?: string;
}

type ImageTitleFormProps = {
  initialData?: FormData;
  onSubmit: (data: FormData) => void;
};

export default function AddsForm({
  initialData,
  onSubmit,
}: ImageTitleFormProps) {
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState<FormData>({
    id: initialData?.id ?? "",
    title: initialData?.title ?? "",
    image: initialData?.image ?? null,
    status: initialData?.status ?? true,
    url: initialData?.url ?? "",
    description: initialData?.description ?? "",
  })

  const [errors, setErrors] = useState<FormErrors>({})

  const previewUrl =
    formData?.image instanceof File ? URL.createObjectURL(formData?.image) : formData?.image;
  
  const isValidHttpUrl = (value: string) => {
    try {
      const u = new URL(value);
      return u.protocol === "http:" || u.protocol === "https:" && u.hostname.includes(".");
    } catch {
      return false;
    }
  };

  const isQuillEmpty = (html: string) => html.replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g,' ').trim().length === 0;
  
  const validate=(form: FormData)=>{
    const errors: FormErrors = {};

    if (!form?.title?.trim()) {
      errors.title = "Title is required";
    }

    if (!form?.image) {
      errors.image = "Image is required";
    }
    else if (form?.image instanceof File) {
      if (form?.image.size > 5 * 1024 * 1024) {
        errors.image = "Image must be smaller than 5 MB";
      }
    }

    if (formData.url?.trim()) {
      if (!isValidHttpUrl(formData.url)) {
        errors.url = "URL must be valid HTTP/HTTPS link";
      }
    }

    if (isQuillEmpty(formData.description ?? "")) {
      errors.description = "Description is required";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  const handleSubmit = async(e: React.FormEvent) => {
    e.preventDefault();

    const { isValid, errors } = validate(formData);

    if (!isValid) {
      setErrors(errors);
      return;
    }

    setLoading(true)
    onSubmit({ 
      id: formData?.id, 
      title: formData?.title, 
      image: formData?.image, 
      status: formData?.status,
      url: formData?.url,
      description: formData?.description,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Image */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Image*  (Recommended: 16:9)
        </label>

        {/* <div className="relative h-80 rounded-xl border border-dashed border-white/20 bg-black/20 overflow-hidden hover:bg-black/30 transition">
          <input
            type="file"
            accept="image/*"
            className="absolute inset-0 z-10 opacity-0 cursor-pointer"
            onChange={e => e.target.files && setImage(e.target.files[0])}
          />

          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Preview"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex flex-col items-center justify-center">
              <Upload className="w-6 h-6 text-cyan-400 mb-2" />
              <p className="text-sm text-white font-medium">Upload Image</p>
              <p className="text-xs text-slate-500 mt-1">
                PNG, JPG, SVG
              </p>
            </div>
          )}
        </div> */}

    <div className="relative w-full aspect-video rounded-xl border border-dashed border-white/20 bg-black/20 overflow-hidden hover:bg-black/30 transition">
      <input
        type="file"
        accept="image/*"
        className="absolute inset-0 z-20 opacity-0 cursor-pointer"
        onChange={e =>
          e.target.files && setFormData({ ...formData, image: e.target.files[0] })
        }
      />

      {previewUrl ? (
        <>
          {/* 16:9 image display */}
          <div className="absolute inset-0">
            {/* soft blurred background */}
            <img
              src={previewUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover blur-md scale-105 opacity-30"
            />

            {/* main preview image */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={previewUrl}
                alt="Preview"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          {/* <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-contain"
            />
          </div> */}

          {/* hover edit overlay */}
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition z-30">
            <Edit2 className="h-8 w-8 text-white" />
            <input
              type="file"
              accept="image/*"
              className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              onChange={e =>
                e.target.files && setFormData({ ...formData, image: e.target.files[0] })
              }
            />
          </div>
        </>
      ) : (
        <div className="h-full w-full flex flex-col items-center justify-center">
          <Upload className="w-6 h-6 text-cyan-400 mb-2" />
          <p className="text-sm text-white font-medium">Upload Image</p>
          <p className="text-xs text-slate-500 mt-1">Recommended: 16:9</p>
        </div>
      )}
    </div>

        {errors.image && (
          <p className="text-sm text-red-500 mt-1">{errors.image}</p>
        )}
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Title*
        </label>
        <input
          type="text"
          value={formData?.title}
          onChange={e => setFormData({...formData, title: e.target.value})}
          placeholder="Enter title"
          className="w-full px-4 py-3 bg-black/20 border border-white rounded-xl focus:outline-none focus:border-cyan-500 text-white text-sm placeholder:text-slate-500 transition-all"
        />
        {errors.title && (
          <p className="text-sm text-red-500 mt-1">{errors.title}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          URL
        </label>
        <input
          type="text"
          value={formData?.url}
          onChange={e => setFormData({...formData, url: e.target.value})}
          placeholder="Enter advertisement external url"
          className="w-full px-4 py-3 bg-black/20 border border-white rounded-xl focus:outline-none focus:border-cyan-500 text-white text-sm placeholder:text-slate-500 transition-all"
        />
        {errors.url && (
          <p className="text-sm text-red-500 mt-1">{errors.url}</p>
        )}
      </div>

      <div className='quill-dark'>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Description*
        </label>
        <QuillEditor
          value={formData?.description ?? ""}
          onChange={(val) =>
            setFormData(prev => ({
              ...prev,
              description: val,
            }))
          }
          placeholder='Enter description'
        />
        {errors.description && (
          <p className="text-sm text-red-500 mt-1">{errors.description}</p>
        )}
      </div>


      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          className="px-6 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium transition"
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}
